import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

/**
 * ImageViewer — component ที่เจ้าหน้าที่ใช้มากที่สุดในหนึ่งวัน
 *
 * ซูมด้วยล้อเมาส์ (จุดที่ชี้อยู่คือจุดศูนย์กลางการซูม) · ลากย้ายภาพ · หมุน 90° ·
 * พอดีหน้าจอ · ขนาดจริง 100% · เปิดเต็มจอ · โหลดภาพถัดไปล่วงหน้า
 *
 * คีย์ลัด: + / - ซูม · 0 พอดีหน้าจอ · 1 ขนาดจริง · R หมุน · F เต็มจอ
 * (R ที่นี่ไม่ชนกับ R = ปฏิเสธ เพราะ DecisionPanel ผูก R ไว้ที่ระดับหน้า
 *  และ ImageViewer รับ R เฉพาะเมื่อ shortcutScope='viewer' — ดู README ตารางคีย์ลัด)
 */
export interface ImageViewerProps {
  src: string;
  alt: string;
  /** URL ของภาพถัดไปในคิว — โหลดล่วงหน้าเพื่อให้กดรายการถัดไปแล้วเห็นภาพทันที */
  preloadSrc?: string;
  /** ข้อความกำกับมุมล่าง เช่น 'สลิปจาก Shopee · 12 ส.ค. 2569' */
  caption?: string;
  /** 'viewer' = รับคีย์ R/F ด้วย · 'zoom-only' = รับเฉพาะ +/-/0/1 */
  shortcutScope?: 'viewer' | 'zoom-only';
  className?: string;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;

export const ImageViewer: React.FC<ImageViewerProps> = ({
  src, alt, preloadSrc, caption, shortcutScope = 'viewer', className,
}) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [fit, setFit] = useState(true);
  const [full, setFull] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  /* รีเซ็ตทุกครั้งที่เปลี่ยนภาพ — ถ้าไม่รีเซ็ต ภาพถัดไปจะเปิดมาซูมค้างจากภาพก่อน */
  useEffect(() => {
    setScale(1); setRotation(0); setOffset({ x: 0, y: 0 }); setFit(true); setLoaded(false);
  }, [src]);

  /* โหลดภาพถัดไปล่วงหน้า */
  useEffect(() => {
    if (!preloadSrc) return;
    const img = new Image();
    img.src = preloadSrc;
  }, [preloadSrc]);

  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    const box = boxRef.current;
    setFit(false);
    setScale((prev) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
      if (box && clientX !== undefined && clientY !== undefined) {
        const rect = box.getBoundingClientRect();
        const cx = clientX - rect.left - rect.width / 2;
        const cy = clientY - rect.top - rect.height / 2;
        const k = next / prev - 1;
        setOffset((o) => ({ x: o.x - cx * k, y: o.y - cy * k }));
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); setFit(true); }, []);
  const actual = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); setFit(false); }, []);
  const rotate = useCallback(() => setRotation((r) => (r + 90) % 360), []);

  /* ล้อเมาส์ต้องผูกแบบ non-passive จึงจะ preventDefault ได้ (กันหน้าเลื่อน) */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      setOffset({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
    };
    const onUp = () => { drag.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  useKeyboardShortcuts([
    { key: '+', description: 'ซูมเข้า', group: 'ดูรูปสลิป', handler: () => zoomAt(1.2) },
    { key: '=', description: 'ซูมเข้า', group: 'ดูรูปสลิป', handler: () => zoomAt(1.2) },
    { key: '-', description: 'ซูมออก', group: 'ดูรูปสลิป', handler: () => zoomAt(1 / 1.2) },
    { key: '0', description: 'พอดีหน้าจอ', group: 'ดูรูปสลิป', handler: reset },
    { key: '1', description: 'ขนาดจริง 100%', group: 'ดูรูปสลิป', handler: actual },
    { key: 'r', description: 'หมุนภาพ 90 องศา', group: 'ดูรูปสลิป', handler: rotate, disabled: shortcutScope !== 'viewer' },
    { key: 'f', description: 'ดูเต็มจอ', group: 'ดูรูปสลิป', handler: () => setFull((v) => !v), disabled: shortcutScope !== 'viewer' },
    { key: 'Escape', description: 'ออกจากโหมดเต็มจอ', group: 'ดูรูปสลิป', handler: () => setFull(false), disabled: !full },
  ]);

  const toolBtn =
    'inline-flex h-[var(--hit-control)] w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] hover:text-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1';

  const body = (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)]',
        full && 'fixed inset-[var(--space-4)] z-[var(--z-modal)] rounded-[var(--radius-lg)] shadow-[var(--elevation-modal)]',
        className,
      )}
    >
      <div className="flex items-center gap-[var(--space-1)] border-b border-[var(--line-200)] bg-[var(--surface-100)] px-[var(--space-2)] py-[var(--space-1)] font-[family-name:var(--font-thai)]">
        <button type="button" className={toolBtn} onClick={() => zoomAt(1 / 1.2)} aria-label="ซูมออก (-)"><Icon name="zoom_out" size={20} /></button>
        <span className="bo-num w-[52px] text-center text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{Math.round(scale * 100)}%</span>
        <button type="button" className={toolBtn} onClick={() => zoomAt(1.2)} aria-label="ซูมเข้า (+)"><Icon name="zoom_in" size={20} /></button>
        <span aria-hidden className="mx-[var(--space-1)] h-5 w-px bg-[var(--line-300)]" />
        <button type="button" className={toolBtn} onClick={reset} aria-label="พอดีหน้าจอ (0)"><Icon name="fit_screen" size={20} /></button>
        <button type="button" className={toolBtn} onClick={actual} aria-label="ขนาดจริง 100% (1)"><Icon name="crop_free" size={20} /></button>
        <button type="button" className={toolBtn} onClick={rotate} aria-label="หมุน 90 องศา (R)"><Icon name="rotate_right" size={20} /></button>
        <span aria-hidden className="mx-[var(--space-1)] h-5 w-px bg-[var(--line-300)]" />
        <button type="button" className={toolBtn} onClick={() => setFull((v) => !v)} aria-label={full ? 'ออกจากเต็มจอ (Esc)' : 'ดูเต็มจอ (F)'}>
          <Icon name={full ? 'close_fullscreen' : 'open_in_full'} size={20} />
        </button>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className={cn(toolBtn, 'ml-auto no-underline')}
          aria-label="เปิดภาพต้นฉบับในแท็บใหม่"
        >
          <Icon name="open_in_new" size={20} />
        </a>
      </div>

      <div
        ref={boxRef}
        onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y }; }}
        className="relative min-h-0 flex-1 overflow-hidden bg-[var(--surface-100)]"
        style={{ cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-600)]">
            กำลังโหลดรูป…
          </div>
        )}
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => setLoaded(true)}
          className="absolute left-1/2 top-1/2 select-none"
          style={{
            transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center',
            maxWidth: fit ? '100%' : 'none',
            maxHeight: fit ? '100%' : 'none',
            transition: drag.current ? 'none' : 'transform var(--duration-fast) var(--ease-standard)',
            opacity: loaded ? 1 : 0,
          }}
        />
      </div>

      {caption && (
        <p className="border-t border-[var(--line-200)] px-[var(--space-3)] py-[var(--space-15)] font-[family-name:var(--font-thai)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">
          {caption}
        </p>
      )}
    </div>
  );

  if (!full) return body;
  return (
    <>
      <div className="fixed inset-0 z-[var(--z-overlay)] bg-[var(--scrim)]" onClick={() => setFull(false)} />
      {body}
    </>
  );
};

/** เทียบสองภาพข้างกัน — ใช้กับกรณีสลิปซ้ำ (FR-ADM-12) */
export const ImageCompare: React.FC<{
  left: { src: string; alt: string; caption?: string };
  right: { src: string; alt: string; caption?: string };
  className?: string;
}> = ({ left, right, className }) => (
  <div className={cn('grid min-h-0 grid-cols-2 gap-[var(--space-3)]', className)}>
    <ImageViewer {...left} shortcutScope="zoom-only" />
    <ImageViewer {...right} shortcutScope="zoom-only" />
  </div>
);
